import { ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import * as moment from 'moment';
import { forkJoin } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { EstacaoService } from 'src/services/estacao.service';
import { MonitoramentoService } from 'src/services/monitoramento.service';
import { PermissoesService } from 'src/services/permissoes.service';
import { ResumoEventoDiaService } from 'src/services/resumo-evento.service';
import { normalizarEventoDialog } from 'src/app/shared/evento-total';

@Component({
  selector: 'app-eventos-monitoramentos-dialog',
  templateUrl: './eventos-monitoramentos-dialog.component.html',
  styleUrls: ['./eventos-monitoramentos-dialog.component.scss'],
  standalone: false
})
export class EventosMonitoramentosDialogComponent {
  @Output() saved = new EventEmitter<void>();

  visible: boolean = false;
  modalMonitoramentoPronto: boolean = false;
  loading: boolean = false;
  loadingEnviarEmail: boolean = false;
  eventosDataSelecionada: any[] = [];
  data_monitoramento: any;
  tituloModal: any;
  estacaoSelecionada: any;
  estacaoDescricao: string = '';
  modoMonitoramentoAtivo: boolean = false;
  novoMonitoramento: any = {
    id: null,
    data_hora_monitoramento: null,
    data_hora_ultima_situacao: null,
    observacao_abertura: '',
    estacao_id: null,
    status: null,
    eventos: [],
    monitoramento_interno: false
  };
  monitoramentoExistente: any = null;
  rowModalContext: any = null;
  minDateEvento: Date | null = null;
  maxDate: Date | null = null;
  maxDateEvento: Date | null = null;
  private dataFimMonitoramentoAnterior: Date | null = null;
  private textoInputDetached = false;

  // Estado de UI cacheado para evitar métodos no template a cada tecla
  monitoramentoFechado = false;
  monitoramentoSemDataFim = true;
  podeSalvar = false;
  podeSalvarEncerrar = false;
  podeEnviarEmailUi = false;

  constructor(
    protected _estacaoService: EstacaoService,
    protected _resumoEventoService: ResumoEventoDiaService,
    protected _monitoramentoService: MonitoramentoService,
    protected permissaoService: PermissoesService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
  ) { }

  trackByEvento(_index: number, row: any): any {
    return row?.tipo_evento_id ?? row?.monitoramento_evento_id ?? _index;
  }

  /** Evita re-render do dialog (datepickers etc.) a cada tecla nos campos de texto. */
  onCampoTextoFocus(): void {
    if (!this.textoInputDetached) {
      this.cdr.detach();
      this.textoInputDetached = true;
    }
  }

  onCampoTextoBlur(): void {
    if (this.textoInputDetached) {
      this.cdr.reattach();
      this.textoInputDetached = false;
      this.atualizarEstadoUiMonitoramento();
      this.cdr.detectChanges();
    }
  }

  atualizarEstadoUiMonitoramento(): void {
    this.monitoramentoFechado = this.monitoramentoEstaFechado();
    this.monitoramentoSemDataFim = !this.monitoramentoTemDataFim();
    this.podeSalvar = this.validarCamposMonitoramento();
    this.podeSalvarEncerrar = this.validarCamposMonitoramentoParaFinalizar();
    this.podeEnviarEmailUi = this.podeEnviarEmail();

    for (const evento of this.eventosDataSelecionada) {
      if (evento.selecionadoParaMonitoramento) {
        evento.minDateFim = this.getLimiteMinFimEvento(evento);
        evento.emAberto = this.eventoEstaEmAberto(evento);
        evento.iconeCss = this.getIconeEvento(evento);
      } else if (!evento.iconeCss) {
        evento.iconeCss = this.getIconeEvento(evento);
      }
    }
  }

  getIconeEvento(row: any): string {
    if (Number(row?.tipo_evento_id) === 9) {
      return 'pi pi-exclamation-triangle';
    }
    return row?.tipo_evento?.icone || row?.icone || '';
  }

  abrirPorEstacaoDia(col: any, row: any): void {
    this.modalMonitoramentoPronto = false;
    this.visible = false;
    this.limparLimitesDatasModal();
    this.resetarSelecoesEventos();

    this.data_monitoramento = col.data_resumo;
    this.tituloModal = `${row.descricao} | ${col.field}`;
    this.estacaoSelecionada = row.estacao_id;
    this.estacaoDescricao = row.descricao || '';
    this.rowModalContext = row;

    const diaCol = moment(col.data_resumo).format('YYYY-MM-DD');
    const dataFormatada = moment(this.data_monitoramento).format('YYYY-MM-DD');

    this._resumoEventoService.mapDialog(this.estacaoSelecionada, dataFormatada).subscribe({
      next: (mapDialog: any) => {
        const eventos = this.montarEventosModalAPartirDoMapDialog(mapDialog?.eventos || [], dataFormatada);
        this.eventosDataSelecionada = this.aplicarConfiguracaoEventoNoModal(
          eventos,
          row.configuracoes_evento || []
        );
        this.carregarMonitoramentosNoModal(dataFormatada, row);
      },
      error: () => {
        const eventosDia = (row.eventos || [])
          .filter((x: any) => moment(x.data_resumo).format('YYYY-MM-DD') === diaCol)
          .map((evento: any) => normalizarEventoDialog(evento));
        this.eventosDataSelecionada = this.aplicarConfiguracaoEventoNoModal(
          eventosDia,
          row.configuracoes_evento || []
        );
        this.carregarMonitoramentosNoModal(dataFormatada, row);
      }
    });
  }

  private montarEventosModalAPartirDoMapDialog(eventos: any[], dataResumo: string): any[] {
    return (eventos || []).map((evento: any) => normalizarEventoDialog({
      ...evento,
      data_resumo: dataResumo,
      selecionadoParaMonitoramento: false,
      data_hora_inicio: null,
      data_hora_fim: null,
      problema: '',
      desfecho: '',
      monitoramento_evento_id: null,
    }));
  }

  abrirNovoPorEstacao(params: {
    estacaoId: number;
    estacaoDescricao: string;
    dataResumo: string;
  }): void {
    this.modalMonitoramentoPronto = false;
    this.visible = false;
    this.loading = true;
    this.limparLimitesDatasModal();
    this.resetarSelecoesEventos();
    this.limparNovoMonitoramento();

    this.estacaoSelecionada = params.estacaoId;
    this.estacaoDescricao = params.estacaoDescricao || '';
    this.data_monitoramento = params.dataResumo;
    this.tituloModal = `${this.estacaoDescricao} | ${moment(params.dataResumo).format('DD/MM/YYYY')}`;

    forkJoin({
      mapDialog: this._resumoEventoService.mapDialog(params.estacaoId, params.dataResumo),
      formData: this._estacaoService.formData(params.estacaoId),
    }).subscribe({
      next: ({ mapDialog, formData }) => {
        const configuracoes = formData?.estacao?.configuracoes_evento || [];
        const descricao = mapDialog?.descricao || params.estacaoDescricao || 'Estação';
        this.estacaoDescricao = descricao;
        this.tituloModal = `${descricao} | ${moment(params.dataResumo).format('DD/MM/YYYY')}`;

        const row = {
          estacao_id: params.estacaoId,
          descricao,
          configuracoes_evento: configuracoes,
        };
        this.rowModalContext = row;

        const eventos = this.montarEventosModalAPartirDoMapDialog(mapDialog?.eventos || [], params.dataResumo);
        this.eventosDataSelecionada = this.aplicarConfiguracaoEventoNoModal(eventos, configuracoes);
        this.loading = false;
        this.carregarMonitoramentosNoModal(params.dataResumo, row, { forcarNovo: true });
      },
      error: () => {
        this.loading = false;
        this.visible = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar dados para o novo monitoramento'
        });
      }
    });
  }

  private carregarMonitoramentosNoModal(
    dataFormatada: string,
    row: any,
    opcoes?: { forcarNovo?: boolean }
  ): void {
    this._estacaoService.getMonitoramentos(this.estacaoSelecionada, dataFormatada).subscribe({
      next: (monitoramentos: any[]) => {
        this.monitoramentoExistente = null;
        this.limparNovoMonitoramento();

        const forcarNovo = !!opcoes?.forcarNovo;
        const aberto = (monitoramentos || []).find((item: any) => Number(item.status) === 0);

        if (forcarNovo) {
          if (aberto) {
            this.messageService.add({
              severity: 'info',
              summary: 'Monitoramento existente',
              detail: 'Existe um monitoramento aberto para esta estação. Carregando os dados existentes.'
            });
            this.aplicarMonitoramentoNoModal(aberto, row);
            this.visible = true;
            return;
          }

          this.prepararFormularioNovoMonitoramento(dataFormatada);
          this.visible = true;
          return;
        }

        if (monitoramentos && monitoramentos.length > 0) {
          const monitoramento = this.escolherMonitoramentoParaModal(monitoramentos);
          this.aplicarMonitoramentoNoModal(monitoramento, row);
        } else {
          this.limparNovoMonitoramento();
          this.resetarSelecoesEventos();

          this._monitoramentoService.validarAbertura(
            this.estacaoSelecionada,
            moment(this.data_monitoramento).format('YYYY-MM-DD HH:mm:ss'),
            null
          ).subscribe({
            next: (resultado: any) => {
              if (!resultado?.pode_abrir && resultado?.motivo === 'monitoramento_aberto_existente' && resultado?.monitoramento_existente) {
                this.messageService.add({
                  severity: 'info',
                  summary: 'Monitoramento existente',
                  detail: 'Existe um monitoramento aberto para esta estação. Carregando os dados existentes.'
                });
                this.aplicarMonitoramentoNoModal(resultado.monitoramento_existente, row);
              } else {
                this.atualizarLimitesDatasModal();
                this.modalMonitoramentoPronto = true;
              }
              this.visible = true;
            },
            error: () => {
              this.atualizarLimitesDatasModal();
              this.modalMonitoramentoPronto = true;
              this.visible = true;
            }
          });
          return;
        }

        this.visible = true;
      },
      error: (err) => {
        console.error('Erro ao carregar monitoramentos:', err);
        this.modalMonitoramentoPronto = true;
        this.visible = true;
      }
    });
  }

  abrirPorMonitoramento(params: {
    estacaoId: number;
    estacaoDescricao: string;
    dataResumo: string;
    monitoramentoId: number;
  }): void {
    this.modalMonitoramentoPronto = false;
    this.visible = false;
    this.limparLimitesDatasModal();
    this.loading = true;
    this.resetarSelecoesEventos();
    this.limparNovoMonitoramento();

    this.estacaoSelecionada = params.estacaoId;
    this.estacaoDescricao = params.estacaoDescricao || '';
    this.data_monitoramento = params.dataResumo;
    this.tituloModal = `${this.estacaoDescricao} | ${moment(params.dataResumo).format('DD/MM/YYYY')}`;

    forkJoin({
      mapDialog: this._resumoEventoService.mapDialog(params.estacaoId, params.dataResumo),
      formData: this._estacaoService.formData(params.estacaoId),
      detalhes: this._monitoramentoService.obterDetalhes(params.monitoramentoId),
    }).subscribe({
      next: ({ mapDialog, formData, detalhes }) => {
        const configuracoes = formData?.estacao?.configuracoes_evento || [];
        const descricao = mapDialog?.descricao || params.estacaoDescricao || 'Estação';
        this.estacaoDescricao = descricao;
        this.tituloModal = `${descricao} | ${moment(params.dataResumo).format('DD/MM/YYYY')}`;

        const row = {
          estacao_id: params.estacaoId,
          descricao,
          configuracoes_evento: configuracoes,
        };
        this.rowModalContext = row;

        const eventos = this.montarEventosModalAPartirDoMapDialog(mapDialog?.eventos || [], params.dataResumo);

        this.eventosDataSelecionada = this.aplicarConfiguracaoEventoNoModal(eventos, configuracoes);
        this.aplicarMonitoramentoNoModal(detalhes, row);
        this.loading = false;
        this.visible = true;
      },
      error: () => {
        this.loading = false;
        this.visible = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao carregar dados do monitoramento'
        });
      }
    });
  }

  private escolherMonitoramentoParaModal(monitoramentos: any[]): any | null {
    if (!monitoramentos?.length) {
      return null;
    }
    const copia = [...monitoramentos];
    copia.sort((a, b) => {
      const sa = Number(a?.status);
      const sb = Number(b?.status);
      if (sa === 0 && sb !== 0) {
        return -1;
      }
      if (sa !== 0 && sb === 0) {
        return 1;
      }
      return Number(b?.id ?? 0) - Number(a?.id ?? 0);
    });
    return copia[0];
  }

  private aplicarConfiguracaoEventoNoModal(eventos: any[], configuracoesEvento: any[] = []): any[] {
    if (!Array.isArray(eventos)) {
      return [];
    }

    return eventos.map((evento: any) => {
      const tipoEventoId = Number(evento?.tipo_evento_id);
      if (tipoEventoId !== 7 && tipoEventoId !== 8) {
        return evento;
      }

      const cfg = (configuracoesEvento || []).find(
        (item: any) => Number(item?.configuracao_evento_id) === tipoEventoId
      );

      if (!cfg) {
        return evento;
      }

      const quantidadeParametroDiario = Number(cfg.quantidade_parametro_diario ?? 0);
      const totalComputadoNormal = Number(
        evento?.total_computado_normal ?? evento?.total_computado ?? 0
      );
      const eventoAtualizado = {
        ...evento,
        maximo_normalidade_eventos: quantidadeParametroDiario
      };

      if (tipoEventoId === 7) {
        eventoAtualizado.disponibilidade = `${totalComputadoNormal}/${quantidadeParametroDiario}`;
        eventoAtualizado.percentual_coleta = quantidadeParametroDiario > 0
          ? `${((totalComputadoNormal / quantidadeParametroDiario) * 100).toFixed(2).replace('.', ',')} %`
          : '0,00 %';
      }

      return eventoAtualizado;
    });
  }

  onSelecionarMonitoramento(evento: any, event: any): void {
    if (!evento.selecionadoParaMonitoramento) {
      this.atualizarEstadoUiMonitoramento();
      return;
    }

    if (!this.eventoTemDataInicio(evento)) {
      evento.data_hora_inicio = this.obterDataInicioPadraoEvento();
    } else if (this.dataHoraEhAntes(evento.data_hora_inicio, this.novoMonitoramento.data_hora_monitoramento)) {
      evento.data_hora_inicio = this.obterDataInicioPadraoEvento();
    }
    evento.problema = evento.problema || '';
    evento.desfecho = evento.desfecho || '';
    this.ajustarInicioEventosAoMonitoramento();
    this.atualizarEstadoUiMonitoramento();
  }

  private obterDataInicioPadraoEvento(): Date {
    const abertura = this.parseDataHora(this.novoMonitoramento?.data_hora_monitoramento);
    return abertura ? new Date(abertura.getTime()) : new Date();
  }

  limparDataFimEvento(evento: any): void {
    evento.data_hora_fim = null;
    evento.emAberto = true;
    this.atualizarEstadoUiMonitoramento();
  }

  limparDataFimMonitoramento(): void {
    this.novoMonitoramento.data_hora_ultima_situacao = null;
    this.onMonitoramentoDataChange();
    this.dataFimMonitoramentoAnterior = null;
  }

  monitoramentoTemDataFim(): boolean {
    return this.dataPreenchida(this.novoMonitoramento?.data_hora_ultima_situacao);
  }

  private obterDataFimMonitoramentoParaLimites(): Date | null {
    return this.parseDataHora(this.novoMonitoramento?.data_hora_ultima_situacao);
  }

  eventoEstaEmAberto(evento: any): boolean {
    return !this.eventoTemDataFim(evento);
  }

  private dataPreenchida(data: any): boolean {
    if (data === null || data === undefined || data === '') {
      return false;
    }
    if (data instanceof Date) {
      return !isNaN(data.getTime());
    }
    return moment(data).isValid();
  }

  eventoTemDataFim(evento: any): boolean {
    return this.dataPreenchida(evento?.data_hora_fim);
  }

  eventoTemDataInicio(evento: any): boolean {
    return this.dataPreenchida(evento?.data_hora_inicio);
  }

  parseDataHora(valor: any): Date | null {
    if (valor === null || valor === undefined || valor === '') {
      return null;
    }
    if (valor instanceof Date) {
      return isNaN(valor.getTime()) ? null : valor;
    }
    if (typeof valor === 'string') {
      const isoLocal = valor.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
      if (isoLocal) {
        const parsedIsoLocal = moment(isoLocal[1], 'YYYY-MM-DDTHH:mm:ss', true);
        if (parsedIsoLocal.isValid()) {
          return parsedIsoLocal.toDate();
        }
      }

      const formatos = [
        moment.ISO_8601,
        'YYYY-MM-DD HH:mm:ss',
        'YYYY-MM-DD HH:mm',
        'DD/MM/YYYY HH:mm:ss',
        'DD/MM/YYYY HH:mm',
      ];
      const parsedComFormato = moment(valor, formatos, true);
      if (parsedComFormato.isValid()) {
        return parsedComFormato.toDate();
      }
    }
    const parsed = moment(valor);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  private truncarSegundosData(valor: any): Date | null {
    const data = this.parseDataHora(valor);
    return data ? moment(data).seconds(0).milliseconds(0).toDate() : null;
  }

  private resolverDataHoraEvento(valorEvento: any, valorMonitoramento: any): Date | null {
    const monRef = this.parseDataHora(valorMonitoramento)
      ?? this.parseDataHora(this.novoMonitoramento?.data_hora_monitoramento);
    const evRef = this.parseDataHora(valorEvento);

    if (!evRef && !monRef) {
      return null;
    }
    if (!evRef) {
      return this.truncarSegundosData(monRef);
    }
    if (!monRef) {
      return this.truncarSegundosData(evRef);
    }

    const ev = moment(evRef).seconds(0).milliseconds(0);
    const mon = moment(monRef).seconds(0).milliseconds(0);
    if (ev.isBefore(mon)) {
      return this.truncarSegundosData(monRef);
    }
    return this.truncarSegundosData(evRef);
  }

  private normalizarMomentDataHora(valor: any): moment.Moment | null {
    const data = this.parseDataHora(valor);
    return data ? moment(data).seconds(0).milliseconds(0) : null;
  }

  private dataHoraEhAntes(valorA: any, valorB: any): boolean {
    const a = this.normalizarMomentDataHora(valorA);
    const b = this.normalizarMomentDataHora(valorB);
    if (!a || !b) {
      return false;
    }
    return a.isBefore(b);
  }

  private dataHoraEhDepois(valorA: any, valorB: any): boolean {
    const a = this.normalizarMomentDataHora(valorA);
    const b = this.normalizarMomentDataHora(valorB);
    if (!a || !b) {
      return false;
    }
    return a.isAfter(b);
  }

  private ajustarInicioEventosAoMonitoramento(): void {
    const monInicio = this.normalizarMomentDataHora(this.novoMonitoramento?.data_hora_monitoramento);
    if (!monInicio) {
      return;
    }

    const monFim = this.normalizarMomentDataHora(this.novoMonitoramento?.data_hora_ultima_situacao);

    for (const evento of this.eventosDataSelecionada) {
      if (!evento.selecionadoParaMonitoramento) {
        continue;
      }

      let evInicio = this.normalizarMomentDataHora(evento.data_hora_inicio);
      if (!evInicio || evInicio.isBefore(monInicio)) {
        evento.data_hora_inicio = this.truncarSegundosData(this.novoMonitoramento?.data_hora_monitoramento);
        continue;
      }
      if (monFim && evInicio.isAfter(monFim)) {
        evento.data_hora_inicio = this.truncarSegundosData(this.novoMonitoramento?.data_hora_ultima_situacao);
        continue;
      }
      evento.data_hora_inicio = this.truncarSegundosData(evento.data_hora_inicio);
    }
  }

  private ajustarFimEventosAoMonitoramento(dataAnterior: Date | null = null): void {
    const monFim = this.truncarSegundosData(this.novoMonitoramento?.data_hora_ultima_situacao);
    if (!monFim) {
      return;
    }

    const eventosComConflito: any[] = [];

    for (const evento of this.eventosDataSelecionada) {
      if (!evento.selecionadoParaMonitoramento) {
        continue;
      }

      if (!this.eventoTemDataFim(evento)) {
        evento.data_hora_fim = new Date(monFim.getTime());
        continue;
      }

      if (this.dataHoraEhDepois(evento.data_hora_fim, monFim)) {
        eventosComConflito.push(evento);
      }
    }

    if (eventosComConflito.length === 0) {
      return;
    }

    const monFimFormatado = moment(monFim).format('DD/MM/YYYY HH:mm');
    const descricoes = eventosComConflito
      .map(e => {
        const evFim = moment(e.data_hora_fim).format('DD/MM/YYYY HH:mm');
        return `"${e.tipo_evento?.descricao}" (${evFim})`;
      })
      .join(', ');

    this.confirmationService.confirm({
      key: 'globalConfirm',
      message: `A data/hora fim do monitoramento (${monFimFormatado}) é anterior à data/hora fim de um ou mais eventos marcados: ${descricoes}. Deseja ajustar a data/hora fim desses eventos para a data/hora fim do monitoramento?`,
      header: 'Conflito de datas',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        for (const evento of eventosComConflito) {
          evento.data_hora_fim = new Date(monFim.getTime());
        }
      },
      reject: () => {
        this.novoMonitoramento.data_hora_ultima_situacao = dataAnterior
          ? new Date(dataAnterior.getTime())
          : null;
        this.dataFimMonitoramentoAnterior = dataAnterior
          ? new Date(dataAnterior.getTime())
          : null;
        this.atualizarLimitesDatasModal();
        this.ajustarInicioEventosAoMonitoramento();
      }
    });
  }

  private limparLimitesDatasModal(): void {
    this.minDateEvento = null;
    this.maxDate = null;
    this.maxDateEvento = null;
    this.dataFimMonitoramentoAnterior = null;
  }

  private atualizarLimitesDatasModal(): void {
    const inicioMon = this.parseDataHora(this.novoMonitoramento?.data_hora_monitoramento);
    const fimMon = this.obterDataFimMonitoramentoParaLimites();
    const hojeFim = moment().endOf('day').toDate();

    this.minDateEvento = this.truncarSegundosData(inicioMon);

    if (fimMon) {
      this.maxDateEvento = new Date(fimMon.getTime());
      this.maxDate = moment(fimMon).endOf('day').toDate();
    } else {
      this.maxDateEvento = hojeFim;
      this.maxDate = hojeFim;
    }

    if (this.minDateEvento && this.maxDateEvento && this.minDateEvento.getTime() > this.maxDateEvento.getTime()) {
      this.maxDateEvento = moment.max(moment(this.minDateEvento), moment()).endOf('day').toDate();
      this.maxDate = this.maxDateEvento;
    }

    this.atualizarEstadoUiMonitoramento();
  }

  getLimiteMinInicioEvento(evento: any): Date | null {
    return this.minDateEvento;
  }

  getLimiteMaxDataEvento(): Date | null {
    return this.maxDateEvento;
  }

  getLimiteMinFimEvento(evento: any): Date | null {
    const candidatos: moment.Moment[] = [];
    const evInicio = this.parseDataHora(evento?.data_hora_inicio);
    const monInicio = this.parseDataHora(this.novoMonitoramento?.data_hora_monitoramento);

    if (evInicio) {
      candidatos.push(moment(evInicio));
    }
    if (monInicio) {
      candidatos.push(moment(monInicio));
    }

    return candidatos.length ? moment.min(candidatos).toDate() : null;
  }

  private recarregarMonitoramentoNoModal(): void {
    if (!this.novoMonitoramento.id || !this.estacaoSelecionada || !this.data_monitoramento) {
      return;
    }

    const dataFormatada = moment(this.data_monitoramento).format('YYYY-MM-DD');
    this._estacaoService.getMonitoramentos(this.estacaoSelecionada, dataFormatada).subscribe({
      next: (monitoramentos: any[]) => {
        if (!monitoramentos?.length) {
          return;
        }

        const monitoramento = monitoramentos.find(
          (item: any) => Number(item.id) === Number(this.novoMonitoramento.id)
        ) || this.escolherMonitoramentoParaModal(monitoramentos);

        if (monitoramento) {
          this.aplicarMonitoramentoNoModal(monitoramento, this.rowModalContext);
        }
      }
    });
  }

  temEventosSelecionados(): boolean {
    return this.eventosDataSelecionada.some(evento => evento.selecionadoParaMonitoramento);
  }

  cancelarMonitoramento(): void {
    if (this.textoInputDetached) {
      this.cdr.reattach();
      this.textoInputDetached = false;
    }
    this.modoMonitoramentoAtivo = false;
    this.limparNovoMonitoramento();
    this.visible = false;
  }

  private prepararFormularioNovoMonitoramento(dataFormatada: string): void {
    this.limparNovoMonitoramento();
    this.resetarSelecoesEventos();

    const dia = moment(dataFormatada, 'YYYY-MM-DD');
    const agora = moment();
    const inicio = dia.isSame(agora, 'day')
      ? agora.clone().seconds(0).milliseconds(0).toDate()
      : dia.clone().startOf('day').toDate();

    this.novoMonitoramento.data_hora_monitoramento = this.truncarSegundosData(inicio);
    this.novoMonitoramento.estacao_id = this.estacaoSelecionada;
    this.atualizarLimitesDatasModal();
    this.modalMonitoramentoPronto = true;
  }

  limparNovoMonitoramento(): void {
    this.novoMonitoramento = {
      id: null,
      data_hora_monitoramento: null,
      data_hora_ultima_situacao: null,
      observacao_abertura: '',
      estacao_id: null,
      status: null,
      eventos: [],
      monitoramento_interno: false
    };
    this.monitoramentoExistente = null;
    this.atualizarEstadoUiMonitoramento();
  }

  validarCamposMonitoramento(): boolean {
    return !!this.novoMonitoramento.data_hora_monitoramento;
  }

  monitoramentoEstaFechado(): boolean {
    const status = this.novoMonitoramento?.status ?? this.monitoramentoExistente?.status;
    return status !== null && status !== undefined && Number(status) !== 0;
  }

  private calcularDataFimAntesProximoMonitoramento(
    inicioAtual: moment.Moment,
    proximoInicio: moment.Moment
  ): Date {
    if (proximoInicio.isSame(inicioAtual, 'day')) {
      return proximoInicio.clone().subtract(1, 'minute').seconds(0).milliseconds(0).toDate();
    }

    const fimSugerido = proximoInicio.clone().subtract(1, 'day').endOf('day').seconds(0).milliseconds(0);
    if (fimSugerido.isBefore(inicioAtual)) {
      return proximoInicio.clone().subtract(1, 'minute').seconds(0).milliseconds(0).toDate();
    }

    return fimSugerido.toDate();
  }

  private tratarConflitoComProximoMonitoramento(resultado: any): boolean {
    if (this.monitoramentoEstaFechado()) {
      return false;
    }

    const proximoMon = resultado?.monitoramento_existente;
    if (!proximoMon?.data_hora_monitoramento) {
      return false;
    }

    const inicioAtual = this.normalizarMomentDataHora(this.novoMonitoramento.data_hora_monitoramento);
    if (!inicioAtual) {
      return false;
    }

    const proximoInicio = moment(proximoMon.data_hora_monitoramento);
    if (!proximoInicio.isAfter(inicioAtual)) {
      return false;
    }

    const novaDataFim = this.calcularDataFimAntesProximoMonitoramento(inicioAtual, proximoInicio);
    if (this.dataHoraEhAntes(novaDataFim, inicioAtual)) {
      return false;
    }

    this.novoMonitoramento.data_hora_ultima_situacao = this.truncarSegundosData(novaDataFim);
    this.atualizarLimitesDatasModal();
    this.dataFimMonitoramentoAnterior = this.truncarSegundosData(novaDataFim);

    this.messageService.add({
      severity: 'info',
      summary: 'Próximo monitoramento',
      detail: `Existe um monitoramento posterior iniciando em ${proximoInicio.format('DD/MM/YYYY HH:mm')}. A data/hora fim foi ajustada automaticamente.`
    });

    return true;
  }

  validarEventosDentroDoMonitoramento(): { valido: boolean; mensagem?: string } {
    const monInicio = this.normalizarMomentDataHora(this.novoMonitoramento.data_hora_monitoramento);
    const monFim = this.normalizarMomentDataHora(this.novoMonitoramento.data_hora_ultima_situacao);

    if (!monInicio) {
      return { valido: false, mensagem: 'Preencha a data/hora de início do monitoramento' };
    }

    if (monFim && this.dataHoraEhAntes(this.novoMonitoramento.data_hora_ultima_situacao, monInicio)) {
      return { valido: false, mensagem: 'A data/hora fim do monitoramento deve ser posterior ao início' };
    }

    const eventosMarcados = this.eventosDataSelecionada.filter(e => e.selecionadoParaMonitoramento);

    for (const evento of eventosMarcados) {
      if (evento.data_hora_inicio && this.dataHoraEhAntes(evento.data_hora_inicio, monInicio)) {
        return { valido: false, mensagem: `A data/hora início do evento "${evento.tipo_evento?.descricao}" deve estar dentro do intervalo do monitoramento` };
      }
      if (monFim && evento.data_hora_fim && this.dataHoraEhDepois(evento.data_hora_fim, monFim)) {
        return { valido: false, mensagem: `A data/hora fim do evento "${evento.tipo_evento?.descricao}" deve estar dentro do intervalo do monitoramento` };
      }
      if (evento.data_hora_inicio && evento.data_hora_fim && this.dataHoraEhAntes(evento.data_hora_fim, evento.data_hora_inicio)) {
        return { valido: false, mensagem: `A data/hora fim do evento "${evento.tipo_evento?.descricao}" deve ser posterior ao início` };
      }
    }

    return { valido: true };
  }

  validarEventosParaEncerrar(): boolean {
    return this.eventosDataSelecionada
      .filter(e => e.selecionadoParaMonitoramento)
      .every(e => this.eventoTemDataFim(e));
  }

  podeEnviarEmail(): boolean {
    if (this.loadingEnviarEmail) {
      return false;
    }
    // Permite reenvio mesmo com monitoramento fechado, se estiver editando um existente
    if (this.novoMonitoramento?.id) {
      return true;
    }
    if (this.temMonitoramentoAtivo()) {
      return true;
    }
    return this.validarCamposMonitoramento() && this.temEventosSelecionados();
  }

  onMonitoramentoDataChange(): void {
    this.atualizarLimitesDatasModal();
    this.ajustarInicioEventosAoMonitoramento();

    if (!this.novoMonitoramento.data_hora_monitoramento || !this.estacaoSelecionada) {
      return;
    }
    if (this.monitoramentoEstaFechado()) {
      return;
    }

    const dataInicio = moment(this.novoMonitoramento.data_hora_monitoramento).format('YYYY-MM-DD HH:mm:ss');
    const dataFim = this.novoMonitoramento.data_hora_ultima_situacao
      ? moment(this.novoMonitoramento.data_hora_ultima_situacao).format('YYYY-MM-DD HH:mm:ss')
      : null;

    this._monitoramentoService.validarAbertura(
      this.estacaoSelecionada,
      dataInicio,
      dataFim,
      this.novoMonitoramento.id
    ).subscribe({
      next: (resultado: any) => {
        if (!resultado?.pode_abrir && resultado?.motivo === 'monitoramento_aberto_existente' && resultado?.monitoramento_existente) {
          this.modalMonitoramentoPronto = false;
          this.messageService.add({
            severity: 'info',
            summary: 'Monitoramento existente',
            detail: 'Já existe um monitoramento aberto para esta estação. Carregando os dados existentes.'
          });
          this.aplicarMonitoramentoNoModal(resultado.monitoramento_existente, this.rowModalContext);
        } else if (!resultado?.pode_abrir && resultado?.motivo === 'conflito_fechado') {
          if (!this.tratarConflitoComProximoMonitoramento(resultado)) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Conflito de intervalo',
              detail: 'O intervalo informado conflita com um monitoramento fechado existente para esta estação.'
            });
          }
        }
      }
    });
  }

  onMonitoramentoFimDataChange(): void {
    const dataAnterior = this.dataFimMonitoramentoAnterior;
    this.onMonitoramentoDataChange();
    this.ajustarFimEventosAoMonitoramento(dataAnterior);
    this.dataFimMonitoramentoAnterior = this.truncarSegundosData(this.novoMonitoramento?.data_hora_ultima_situacao);
  }

  private aplicarMonitoramentoNoModal(monitoramento: any, row: any): void {
    const diaCol = moment(this.data_monitoramento).format('YYYY-MM-DD');

    this.monitoramentoExistente = monitoramento;
    this.novoMonitoramento.id = monitoramento.id;
    this.novoMonitoramento.status = monitoramento.status;
    this.novoMonitoramento.data_hora_monitoramento = this.truncarSegundosData(monitoramento.data_hora_monitoramento);
    this.novoMonitoramento.data_hora_ultima_situacao = Number(monitoramento.status) === 0
      ? null
      : this.parseDataHora(monitoramento.data_hora_ultima_situacao);
    this.novoMonitoramento.observacao_abertura = monitoramento.observacao_abertura || '';
    this.novoMonitoramento.estacao_id = monitoramento.estacao_id;
    this.novoMonitoramento.monitoramento_interno = monitoramento.monitoramento_interno || false;

    this.eventosDataSelecionada.forEach(evento => {
      evento.selecionadoParaMonitoramento = false;
      evento.data_hora_inicio = null;
      evento.data_hora_fim = null;
      evento.problema = '';
      evento.desfecho = '';
      evento.monitoramento_evento_id = null;
    });

    if (monitoramento.eventos && monitoramento.eventos.length > 0) {
      monitoramento.eventos.forEach((eventoMonitoramento: any) => {
        const tipoId = Number(eventoMonitoramento.tipo_evento_id);
        const evento = this.eventosDataSelecionada.find(
          (e: any) => Number(e.tipo_evento_id) === tipoId
        );

        if (evento) {
          evento.selecionadoParaMonitoramento = true;
          evento.data_hora_inicio = this.resolverDataHoraEvento(
            eventoMonitoramento.data_hora_inicio,
            monitoramento.data_hora_monitoramento
          );
          evento.data_hora_fim = this.parseDataHora(eventoMonitoramento.data_hora_fim);
          evento.problema = eventoMonitoramento.problema || '';
          evento.desfecho = eventoMonitoramento.desfecho || '';
          evento.monitoramento_evento_id = eventoMonitoramento.id;
        } else if (eventoMonitoramento.tipo_evento || eventoMonitoramento.tipo_evento_descricao) {
          const configs = row?.configuracoes_evento || [];
          const cfg = configs.find((c: any) => Number(c.configuracao_evento_id) === tipoId);
          const tipoEvento = eventoMonitoramento.tipo_evento || {
            descricao: eventoMonitoramento.tipo_evento_descricao,
            icone: eventoMonitoramento.tipo_evento_icone,
          };
          if (this.eventosDataSelecionada.some((e: any) => Number(e.tipo_evento_id) === tipoId)) {
            return;
          }
          const novoEvento: any = {
            data_resumo: diaCol,
            tipo_evento_id: tipoId,
            tipo_evento: tipoEvento,
            maximo_normalidade_eventos: cfg?.quantidade_parametro_diario ?? 0,
            total_computado: 0,
            situaca_evento: 0,
            tooltip: '',
            cor: '#6c8f3f',
            selecionadoParaMonitoramento: true,
            data_hora_inicio: this.resolverDataHoraEvento(
              eventoMonitoramento.data_hora_inicio,
              monitoramento.data_hora_monitoramento
            ),
            data_hora_fim: this.parseDataHora(eventoMonitoramento.data_hora_fim),
            problema: eventoMonitoramento.problema || '',
            desfecho: eventoMonitoramento.desfecho || '',
            monitoramento_evento_id: eventoMonitoramento.id,
            disponibilidade: null,
            percentual_coleta: null,
            percentual_integracao: null,
            atraso_medio_integracao: null
          };
          this.eventosDataSelecionada.push(novoEvento);
        }
      });
      this.eventosDataSelecionada.sort(
        (a: any, b: any) => Number(a.tipo_evento_id) - Number(b.tipo_evento_id)
      );
    }

    this.ajustarInicioEventosAoMonitoramento();
    this.atualizarLimitesDatasModal();
    this.dataFimMonitoramentoAnterior = this.truncarSegundosData(this.novoMonitoramento?.data_hora_ultima_situacao);
    this.modalMonitoramentoPronto = true;
    this.atualizarEstadoUiMonitoramento();
  }

  private validarAberturaAntesSalvar(tipo: 'abrir' | 'encerrar'): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.novoMonitoramento.data_hora_monitoramento || !this.estacaoSelecionada) {
        resolve(true);
        return;
      }

      const dataInicio = moment(this.novoMonitoramento.data_hora_monitoramento).format('YYYY-MM-DD HH:mm:ss');
      const dataFim = this.novoMonitoramento.data_hora_ultima_situacao
        ? moment(this.novoMonitoramento.data_hora_ultima_situacao).format('YYYY-MM-DD HH:mm:ss')
        : (tipo === 'encerrar' ? dataInicio : moment().format('YYYY-MM-DD HH:mm:ss'));

      this._monitoramentoService.validarAbertura(
        this.estacaoSelecionada,
        dataInicio,
        dataFim,
        this.novoMonitoramento.id
      ).subscribe({
        next: (resultado: any) => {
          if (!resultado?.pode_abrir && resultado?.motivo === 'monitoramento_aberto_existente' && resultado?.monitoramento_existente) {
            this.modalMonitoramentoPronto = false;
            this.messageService.add({
              severity: 'warn',
              summary: 'Monitoramento aberto',
              detail: 'Já existe um monitoramento aberto para esta estação. Carregando os dados existentes.'
            });
            this.aplicarMonitoramentoNoModal(resultado.monitoramento_existente, this.rowModalContext);
            resolve(false);
            return;
          }
          if (!resultado?.pode_abrir && resultado?.motivo === 'conflito_fechado') {
            if (this.tratarConflitoComProximoMonitoramento(resultado)) {
              resolve(true);
              return;
            }
            this.messageService.add({
              severity: 'error',
              summary: 'Conflito de intervalo',
              detail: 'O intervalo informado conflita com um monitoramento fechado existente para esta estação.'
            });
            resolve(false);
            return;
          }
          resolve(true);
        },
        error: () => resolve(true)
      });
    });
  }

  validarCamposMonitoramentoParaFinalizar(): boolean {
    return !!this.novoMonitoramento.data_hora_monitoramento &&
      !!this.novoMonitoramento.data_hora_ultima_situacao;
  }

  temMonitoramentoAtivo(): boolean {
    if (!this.estacaoSelecionada) {
      return false;
    }

    if (this.monitoramentoExistente && this.monitoramentoExistente.status === 0) {
      return true;
    }

    if (this.novoMonitoramento.id && this.novoMonitoramento.status === 0) {
      return true;
    }

    return false;
  }

  private garantirChangeDetectionAtivo(): void {
    if (this.textoInputDetached) {
      this.cdr.reattach();
      this.textoInputDetached = false;
      this.atualizarEstadoUiMonitoramento();
    }
  }

  enviarEmailEstacao(): void {
    this.garantirChangeDetectionAtivo();

    if (!this.estacaoSelecionada) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione uma estação para enviar o e-mail'
      });
      return;
    }

    const temDadosValidos = this.validarCamposMonitoramento() &&
      this.eventosDataSelecionada.some(e => e.selecionadoParaMonitoramento);
    // Persiste alterações antes do envio quando aberto; fechado só reenvia o e-mail
    const precisaSalvar = temDadosValidos && !this.monitoramentoEstaFechado();
    const temMonitoramentoSalvo = !!this.novoMonitoramento?.id;

    if (precisaSalvar && !this.validarCamposMonitoramento()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todas as datas obrigatórias do monitoramento antes de enviar o e-mail'
      });
      return;
    }

    if (precisaSalvar && !this.eventosDataSelecionada.some(e => e.selecionadoParaMonitoramento)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione pelo menos um evento antes de enviar o e-mail'
      });
      return;
    }

    if (!precisaSalvar && !this.temMonitoramentoAtivo() && !temMonitoramentoSalvo) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não há monitoramento ativo para esta estação'
      });
      return;
    }

    const nomeEstacao = this.estacaoDescricao || `ID: ${this.estacaoSelecionada}`;

    this.confirmationService.confirm({
      key: 'globalConfirm',
      message:
        `Deseja realmente enviar e-mail de monitoramento para a estação "${nomeEstacao}"?<br>` +
        `O e-mail será enviado para todos os grupos de e-mail relacionados a esta estação.`,
      header: 'Confirmar Envio de E-mail',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.loadingEnviarEmail = true;
        this.atualizarEstadoUiMonitoramento();

        if (precisaSalvar) {
          this.salvarMonitoramentoEEnviarEmail();
        } else {
          this.executarEnvioEmail();
        }
      },
      reject: () => { }
    });
  }

  salvarMonitoramentoEEnviarEmail(): void {
    this.ajustarInicioEventosAoMonitoramento();
    const validacaoEventos = this.validarEventosDentroDoMonitoramento();
    if (!validacaoEventos.valido) {
      this.loadingEnviarEmail = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: validacaoEventos.mensagem
      });
      return;
    }

    const eventosSelecionados = this.montarEventosSelecionados('abrir');
    if (eventosSelecionados.length === 0) {
      this.loadingEnviarEmail = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Selecione pelo menos um evento antes de enviar o e-mail'
      });
      return;
    }

    this.validarAberturaAntesSalvar('abrir').then((podeSalvar) => {
      if (!podeSalvar) {
        this.loadingEnviarEmail = false;
        return;
      }
      this.persistirMonitoramento('abrir', eventosSelecionados, () => {
        this.executarEnvioEmail();
      });
    });
  }

  executarEnvioEmail(): void {
    this._monitoramentoService.enviarEmailEstacao(
      this.estacaoSelecionada,
      null,
      this.novoMonitoramento?.id ?? null
    ).subscribe({
      next: (resultado: any) => {
        this.loadingEnviarEmail = false;

        if (resultado.sucesso) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: `${resultado.mensagem || 'E-mail enviado com sucesso'}. Grupos encontrados: ${resultado.grupos_encontrados || 0}, E-mails enviados: ${resultado.emails_enviados || 0}${resultado.emails_com_erro > 0 ? `, E-mails com erro: ${resultado.emails_com_erro}` : ''}`
          });
        } else {
          this.messageService.add({
            severity: 'info',
            summary: 'Informação',
            detail: resultado.mensagem || 'Nenhum grupo de e-mail encontrado para esta estação. Verifique o cadastro em Monitoramento > Grupos de E-mail.'
          });
        }
        this.atualizarEstadoUiMonitoramento();
      },
      error: (err) => {
        this.loadingEnviarEmail = false;
        this.atualizarEstadoUiMonitoramento();
        const errorMsg = err?.error?.message || 'Erro ao enviar e-mail';
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: errorMsg
        });
      }
    });
  }

  salvarMonitoramento(tipo: 'abrir' | 'encerrar'): void {
    if (tipo === 'encerrar') {
      if (!this.validarCamposMonitoramentoParaFinalizar()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Preencha todas as datas obrigatórias para finalizar o monitoramento'
        });
        return;
      }
      if (!this.validarEventosParaEncerrar()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Todos os eventos marcados devem ter data/hora de encerramento para finalizar o monitoramento'
        });
        return;
      }
    } else {
      if (!this.validarCamposMonitoramento()) {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Preencha a data/hora de início do monitoramento'
        });
        return;
      }
    }

    this.ajustarInicioEventosAoMonitoramento();
    const validacaoEventos = this.validarEventosDentroDoMonitoramento();
    if (!validacaoEventos.valido) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: validacaoEventos.mensagem
      });
      return;
    }

    const eventosSelecionados = this.montarEventosSelecionados(tipo);
    if (eventosSelecionados.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Selecione pelo menos um evento'
      });
      return;
    }

    this.validarAberturaAntesSalvar(tipo).then((podeSalvar) => {
      if (!podeSalvar) {
        return;
      }
      this.persistirMonitoramento(tipo, eventosSelecionados);
    });
  }

  private montarEventosSelecionados(tipo: 'abrir' | 'encerrar'): any[] {
    return this.eventosDataSelecionada
      .filter(evento => evento.selecionadoParaMonitoramento)
      .map(evento => ({
        id: evento.monitoramento_evento_id || null,
        tipo_evento_id: evento.tipo_evento_id,
        data_hora_inicio: this.eventoTemDataInicio(evento)
          ? moment(evento.data_hora_inicio).format('YYYY-MM-DD HH:mm:ss')
          : null,
        data_hora_fim: this.eventoTemDataFim(evento)
          ? moment(evento.data_hora_fim).format('YYYY-MM-DD HH:mm:ss')
          : null,
        problema: (evento.problema ?? '').toString().trim(),
        desfecho: (evento.desfecho ?? '').toString().trim(),
        status: tipo === 'abrir' ? 0 : 1
      }));
  }

  private persistirMonitoramento(tipo: 'abrir' | 'encerrar', eventosSelecionados: any[], callback?: () => void): void {
    const existeMonitoramento = this.novoMonitoramento.id !== null && this.novoMonitoramento.id !== undefined;

    if (existeMonitoramento) {
      const payloadAtualizacao: any = {
        status: tipo === 'abrir' ? 0 : 1,
        observacao_abertura: this.novoMonitoramento.observacao_abertura,
        observacao_fechamento: tipo === 'encerrar' ? (this.novoMonitoramento.observacao_fechamento || '') : null,
        monitoramento_interno: this.novoMonitoramento.monitoramento_interno || false,
        eventos: eventosSelecionados
      };

      if (this.novoMonitoramento.data_hora_monitoramento) {
        payloadAtualizacao.data_hora_monitoramento = moment(this.novoMonitoramento.data_hora_monitoramento).format('YYYY-MM-DD HH:mm:ss');
      }

      if (this.novoMonitoramento.data_hora_ultima_situacao) {
        payloadAtualizacao.data_hora_ultima_situacao = moment(this.novoMonitoramento.data_hora_ultima_situacao).format('YYYY-MM-DD HH:mm:ss');
      }

      this._monitoramentoService.atualizar(this.novoMonitoramento.id, payloadAtualizacao).subscribe({
        next: () => {
          this.novoMonitoramento.status = tipo === 'abrir' ? 0 : 1;
          if (this.monitoramentoExistente) {
            this.monitoramentoExistente.status = tipo === 'abrir' ? 0 : 1;
          }
          this.finalizarAtualizacao(tipo, eventosSelecionados.length, 0, callback);
        },
        error: () => {
          if (callback) {
            this.loadingEnviarEmail = false;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível atualizar o monitoramento'
          });
        }
      });
      return;
    }

    const payload: any = {
      estacao_id: this.estacaoSelecionada,
      status: tipo === 'abrir' ? 0 : 1,
      data_hora_monitoramento: moment(this.novoMonitoramento.data_hora_monitoramento).format('YYYY-MM-DD HH:mm:ss'),
      observacao_abertura: this.novoMonitoramento.observacao_abertura,
      monitoramento_interno: this.novoMonitoramento.monitoramento_interno || false,
      eventos: eventosSelecionados.map(e => ({
        tipo_evento_id: e.tipo_evento_id,
        data_hora_inicio: e.data_hora_inicio,
        data_hora_fim: e.data_hora_fim,
        status: e.status,
        problema: e.problema,
        desfecho: e.desfecho
      }))
    };

    if (this.novoMonitoramento.data_hora_ultima_situacao) {
      payload.data_hora_ultima_situacao = moment(this.novoMonitoramento.data_hora_ultima_situacao).format('YYYY-MM-DD HH:mm:ss');
    }

    this._monitoramentoService.criarEmLote(payload).subscribe({
      next: (response) => {
        if (response?.id) {
          this.novoMonitoramento.id = response.id;
          this.novoMonitoramento.status = tipo === 'abrir' ? 0 : 1;
          this.monitoramentoExistente = { ...this.novoMonitoramento };
          this.sincronizarIdsEventosAposCriacao(response);
          if (tipo === 'abrir' && this.rowModalContext) {
            this.aplicarMonitoramentoNoModal(response, this.rowModalContext);
          }
        }

        if (callback) {
          callback();
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: `Monitoramento ${tipo === 'abrir' ? 'salvo' : 'encerrado'} com ${eventosSelecionados.length} evento(s)`
        });

        if (tipo === 'encerrar') {
          this.modoMonitoramentoAtivo = false;
          this.limparNovoMonitoramento();
          this.resetarSelecoesEventos();
          this.visible = false;
          this.saved.emit();
        } else {
          this.visible = false;
          this.saved.emit();
        }
      },
      error: () => {
        if (callback) {
          this.loadingEnviarEmail = false;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Não foi possível criar o monitoramento'
        });
      }
    });
  }

  private sincronizarIdsEventosAposCriacao(response: any): void {
    const eventosResposta = response?.eventos || [];
    eventosResposta.forEach((eventoSalvo: any) => {
      const eventoLocal = this.eventosDataSelecionada.find(
        (e: any) => Number(e.tipo_evento_id) === Number(eventoSalvo.tipo_evento_id)
      );
      if (eventoLocal) {
        eventoLocal.monitoramento_evento_id = eventoSalvo.id;
      }
    });
  }

  atualizarEventosMonitoramento(eventos: any[], tipo: 'abrir' | 'encerrar', callback?: () => void): void {
    let eventosAtualizados = 0;
    let eventosComErro = 0;
    const totalOperacoes = eventos.length;

    const verificarFim = (): void => {
      if (eventosAtualizados + eventosComErro === totalOperacoes) {
        this.finalizarAtualizacao(tipo, eventosAtualizados, eventosComErro, callback);
      }
    };

    if (totalOperacoes === 0) {
      this.finalizarAtualizacao(tipo, 0, 0, callback);
      return;
    }

    eventos.forEach((evento) => {
      if (evento.id) {
        const payloadEvento = {
          data_hora_inicio: evento.data_hora_inicio,
          data_hora_fim: evento.data_hora_fim,
          status: evento.status,
          problema: evento.problema,
          desfecho: evento.desfecho
        };

        this._monitoramentoService.atualizarEvento(evento.id, payloadEvento).subscribe({
          next: () => {
            eventosAtualizados++;
            verificarFim();
          },
          error: (error) => {
            console.error('Erro ao atualizar evento:', error);
            eventosComErro++;
            verificarFim();
          }
        });
      } else {
        const payloadNovoEvento = {
          monitoramento_id: this.novoMonitoramento.id,
          tipo_evento_id: evento.tipo_evento_id,
          data_hora_inicio: evento.data_hora_inicio,
          data_hora_fim: evento.data_hora_fim,
          status: evento.status,
          problema: evento.problema,
          desfecho: evento.desfecho
        };

        this._monitoramentoService.criarEvento(payloadNovoEvento).subscribe({
          next: (response: any) => {
            const eventoLocal = this.eventosDataSelecionada.find(
              (e: any) => Number(e.tipo_evento_id) === Number(evento.tipo_evento_id)
            );
            if (eventoLocal && response?.id) {
              eventoLocal.monitoramento_evento_id = response.id;
            }
            eventosAtualizados++;
            verificarFim();
          },
          error: (error) => {
            console.error('Erro ao criar evento:', error);
            eventosComErro++;
            verificarFim();
          }
        });
      }
    });
  }

  finalizarAtualizacao(tipo: 'abrir' | 'encerrar', eventosAtualizados: number, eventosComErro: number, callback?: () => void): void {
    if (callback) {
      callback();
      return;
    }

    if (eventosComErro === 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: `Monitoramento ${tipo === 'abrir' ? 'atualizado' : 'encerrado'} com ${eventosAtualizados} evento(s)`
      });
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: `Monitoramento atualizado, mas ${eventosComErro} evento(s) apresentaram erro`
      });
    }

    if (tipo === 'encerrar') {
      this.modoMonitoramentoAtivo = false;
      this.limparNovoMonitoramento();
      this.resetarSelecoesEventos();
      this.visible = false;
      this.saved.emit();
    } else if (eventosComErro === 0) {
      this.visible = false;
      this.saved.emit();
    } else {
      this.saved.emit();
    }
  }

  resetarSelecoesEventos(): void {
    this.eventosDataSelecionada.forEach(evento => {
      evento.selecionadoParaMonitoramento = false;
      evento.data_hora_inicio = null;
      evento.data_hora_fim = null;
      evento.problema = '';
      evento.desfecho = '';
    });
  }
}
